import { Controller } from 'stimulus'
import { materializeLazyCell } from '@helpers'

// Pastes a block of spreadsheet cells (Excel / Google Sheets copy TSV) into the
// inline-editable tables (task results, samples). Attach to the table's scroll
// container. Each editable cell pre-renders its own hidden remote form — or, on
// the account samples grid, materializes one from its column template on
// demand — so every pasted value saves through the same path as a hand-typed
// inline edit (audits, formulas, pass/fail checks, broadcasts).
export default class extends Controller {
  connect() {
    this.boundHandlePaste = this.handlePaste.bind(this)
    this.element.addEventListener('paste', this.boundHandlePaste)
  }

  disconnect() {
    this.element.removeEventListener('paste', this.boundHandlePaste)
  }

  handlePaste(event) {
    const field = event.target
    if (!field.matches || !field.matches('input, select, textarea')) return

    const anchorTd = field.closest('td.inline-editable-form-container')
    if (!anchorTd) return

    const text = (event.clipboardData || window.clipboardData)?.getData('text') || ''
    // Only take over for spreadsheet-shaped content — Excel always appends a
    // trailing newline, even for a single copied cell. Plain text keeps the
    // browser's default insert-at-cursor behavior.
    if (!/[\t\n\r]/.test(text)) return

    event.preventDefault()

    const grid = this.parseGrid(text)
    if (!grid.length) return

    const { entries, skipped } = this.collectEntries(anchorTd, grid)

    this.closeAnchor(field)
    this.processQueue(entries, skipped)
  }

  // Walk the table from the anchor cell: pasted columns map onto the tds to the
  // right (by cell index, so the block stays aligned with the visible columns),
  // pasted rows onto the trs below. Values landing on non-editable cells
  // (formula, wysiwyg, attachments) or past the loaded rows are counted as
  // skipped, mirroring how the block would sit over the grid in Excel.
  collectEntries(anchorTd, grid) {
    const entries = []
    let skipped = 0
    const anchorIndex = anchorTd.cellIndex
    let tr = anchorTd.parentElement

    grid.forEach((row) => {
      row.forEach((raw, col) => {
        const td = tr && tr.cells[anchorIndex + col]
        const editable = td
          && td.matches('td.inline-editable-form-container')
          && td.dataset.itemId
          && (td.matches('[data-controller~="lazy-cell"]')
            || td.querySelector('[data-target~="inline-editable-form.field"]'))

        if (editable) {
          entries.push({ itemId: td.dataset.itemId, raw })
        } else if (raw.trim() !== '') {
          skipped += 1
        }
      })
      tr = tr ? tr.nextElementSibling : null
    })

    return { entries, skipped }
  }

  // The anchor input is focused with its form open. Restore its pre-paste value
  // and blur so the inline editor's blur handler sees a clean field and just
  // closes — the queue below saves the pasted value like any other cell.
  closeAnchor(field) {
    const original = $(field).data('originalValue')
    if (original !== undefined) $(field).val(original)
    field.blur()

    const container = $(field).closest('td.inline-editable-form-container')
    container.find('.form').hide()
    container.find('.value').show()
  }

  // Cells save one at a time — the sequencing is deliberate: it keeps
  // left-to-right order within a row (a sample's material options re-render
  // after its category saves) and avoids hammering the server with a
  // request per cell all at once.
  /* eslint-disable no-restricted-syntax, no-await-in-loop */
  async processQueue(entries, initialSkipped) {
    let applied = 0
    let skipped = initialSkipped

    entries.forEach(entry => this.findTd(entry.itemId)?.classList.add('pending-save'))

    for (const entry of entries) {
      const outcome = await this.applyEntry(entry)
      if (outcome === 'applied') applied += 1
      if (outcome === 'skipped') skipped += 1
    }

    this.notify(applied, skipped)
  }
  /* eslint-enable no-restricted-syntax, no-await-in-loop */

  // Saves one pasted cell. The td is re-resolved by data-item-id right before
  // saving: a broadcast may have replaced it since the paste, and firing a
  // detached form silently no-ops. Returns 'applied', 'skipped', or 'blank'
  // (blank pasted cells that found no editable target don't count as skipped).
  async applyEntry(entry) {
    const missed = entry.raw.trim() === '' ? 'blank' : 'skipped'

    const td = this.findTd(entry.itemId)
    if (!td) return missed
    td.classList.remove('pending-save')

    // Lazy account-grid cells carry no form until first edit — build it now.
    materializeLazyCell(td)

    const field = td.querySelector('[data-target~="inline-editable-form.field"]')
    const form = field && field.closest('form')
    if (!field || !form) return missed

    const value = this.coerceValue(field, entry.raw)
    if (value === null) return missed
    if (`${field.value}` === `${value}`) return 'applied'

    $(field).val(value)
    // Keep the inline editor's dirty-tracking consistent so a later focus/blur
    // of this cell doesn't re-submit or revert the pasted value.
    $(field).data('originalValue', $(field).val())

    td.classList.add('saving')
    await this.submitForm(form)
    return 'applied'
  }

  submitForm(form) {
    return new Promise((resolve) => {
      let timer = null
      const finish = () => {
        clearTimeout(timer)
        form.removeEventListener('ajax:complete', finish)
        resolve()
      }
      form.addEventListener('ajax:complete', finish, { once: true })
      // Never wedge the queue on a lost response
      timer = setTimeout(finish, 10000)
      Rails.fire(form, 'submit')
    })
  }

  notify(applied, skipped) {
    if (!window.toastr) return

    const values = n => `${n} value${n === 1 ? '' : 's'}`
    if (applied && !skipped) {
      window.toastr.success(`Pasted ${values(applied)}`)
    } else if (applied) {
      window.toastr.warning(`Pasted ${values(applied)}, skipped ${values(skipped)}`)
    } else if (skipped) {
      window.toastr.error('Nothing pasted — the copied block did not line up with any editable cells')
    }
  }

  // Match the pasted text to what the cell's input can accept, or return null
  // to skip the cell (never clobber existing data with an unusable value).
  coerceValue(field, raw) {
    const value = raw.trim()
    const tag = field.tagName.toLowerCase()

    if (tag === 'select') {
      if (value === '') return ''
      const match = Array.from(field.options).find(option => (
        option.value === value || option.text.trim().toLowerCase() === value.toLowerCase()
      ))
      return match ? match.value : null
    }

    const type = (field.getAttribute('type') || '').toLowerCase()

    if (type === 'number') {
      if (value === '') return ''
      const cleaned = value.replace(/[$,%\s]/g, '')
      return cleaned !== '' && !Number.isNaN(Number(cleaned)) ? cleaned : null
    }

    if (type === 'date') {
      if (value === '') return ''
      const date = this.parseDate(value)
      return date ? this.isoDate(date) : null
    }

    if (type === 'datetime-local') {
      if (value === '') return ''
      const date = this.parseDate(value)
      return date ? `${this.isoDate(date)}T${this.pad(date.getHours())}:${this.pad(date.getMinutes())}` : null
    }

    return value
  }

  // Excel copies dates as displayed — commonly M/D/YYYY (with optional time /
  // meridiem) — while date inputs only accept ISO. Parse the common shapes
  // in local time; new Date('YYYY-MM-DD') alone would parse as UTC and can
  // shift the day.
  parseDate(value) {
    const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{2}))?/)
    if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3], +(iso[4] || 0), +(iso[5] || 0))

    const us = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:[ ](\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm]?)?)?/)
    if (us) {
      let year = +us[3]
      if (year < 100) year += 2000
      let hours = +(us[4] || 0)
      const meridiem = us[6]
      if (meridiem && /p/i.test(meridiem) && hours < 12) hours += 12
      if (meridiem && /a/i.test(meridiem) && hours === 12) hours = 0
      return new Date(year, +us[1] - 1, +us[2], hours, +(us[5] || 0))
    }

    const fallback = new Date(value)
    return Number.isNaN(fallback.getTime()) ? null : fallback
  }

  isoDate(date) {
    return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())}`
  }

  pad(n) {
    return `${n}`.padStart(2, '0')
  }

  findTd(itemId) {
    return this.element.querySelector(`td[data-item-id="${itemId.replace(/"/g, '\\"')}"]`)
  }

  // TSV as Excel writes it: tabs between cells, newlines between rows, cells
  // containing tabs/newlines/quotes wrapped in double quotes with quotes
  // doubled. A trailing all-blank row (Excel's trailing newline) is dropped.
  parseGrid(text) {
    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    const rows = []
    let row = []
    let cell = ''
    let inQuotes = false

    for (let i = 0; i < normalized.length; i += 1) {
      const char = normalized[i]
      if (inQuotes) {
        if (char === '"') {
          if (normalized[i + 1] === '"') {
            cell += '"'
            i += 1
          } else {
            inQuotes = false
          }
        } else {
          cell += char
        }
      } else if (char === '"' && cell === '') {
        inQuotes = true
      } else if (char === '\t') {
        row.push(cell)
        cell = ''
      } else if (char === '\n') {
        row.push(cell)
        rows.push(row)
        row = []
        cell = ''
      } else {
        cell += char
      }
    }
    if (cell !== '' || row.length) {
      row.push(cell)
      rows.push(row)
    }

    while (rows.length && rows[rows.length - 1].every(c => c === '')) rows.pop()

    return rows
  }
}
